import { createClient, SupabaseClient } from '@supabase/supabase-js';
import { Database } from '../types/supabase';

// Log environment variables for debugging (development only)
if (import.meta.env.DEV) {
  console.log('Supabase environment:', {
    hasUrl: !!import.meta.env.VITE_SUPABASE_URL,
    hasKey: !!import.meta.env.VITE_SUPABASE_ANON_KEY,
    urlLength: import.meta.env.VITE_SUPABASE_URL?.length,
    keyLength: import.meta.env.VITE_SUPABASE_ANON_KEY?.length,
    url: import.meta.env.VITE_SUPABASE_URL?.substring(0, 20) + '...'
  });

  console.log('[DEBUG] VITE_SUPABASE_URL:', import.meta.env.VITE_SUPABASE_URL);
  console.log('[DEBUG] VITE_SUPABASE_ANON_KEY:', import.meta.env.VITE_SUPABASE_ANON_KEY?.slice(0, 10) + '...');
}

const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
const supabaseAnonKey = import.meta.env.VITE_SUPABASE_ANON_KEY;

// Singleton pattern to ensure only one client instance
let supabaseInstance: SupabaseClient<Database> | null = null;

// Create Supabase client with simplified configuration
export const supabase: SupabaseClient<Database> = (() => {
  if (!supabaseInstance) {
    if (!supabaseUrl || !supabaseAnonKey) {
      throw new Error('Missing Supabase environment variables');
    }
    
    supabaseInstance = createClient(supabaseUrl, supabaseAnonKey, {
      auth: {
        autoRefreshToken: true,
        persistSession: true,
        detectSessionInUrl: true,
        flowType: 'pkce'
      },
      db: {
        schema: 'public'
      },
      global: {
        headers: {
          'X-Client-Info': 'idecide-web'
        }
      }
    });

    // Add global error handler and auth state monitoring
    supabaseInstance.auth.onAuthStateChange((event, session) => {
      console.log('🔐 Global auth state changed:', event, session?.user?.id);
      
      // Handle specific auth events
      if (event === 'SIGNED_OUT') {
        console.log('🚪 User signed out, clearing cache');
        clearCache(); // Clear profile cache on sign out
      } else if (event === 'SIGNED_IN' && session?.user) {
        console.log('🔑 User signed in, user ID:', session.user.id);
      } else if (event === 'TOKEN_REFRESHED') {
        console.log('🔄 Token refreshed for user:', session?.user?.id);
      }
    });
  }
  return supabaseInstance;
})();

// Retry function for failed requests
async function retryRequest<T>(
  requestFn: () => Promise<T>,
  maxRetries: number = 3,
  delay: number = 1000
): Promise<T> {
  let lastError: any;
  
  for (let attempt = 1; attempt <= maxRetries; attempt++) {
    try {
      return await requestFn();
    } catch (error) {
      lastError = error;
      console.warn(`Request attempt ${attempt} failed:`, error);
      
      if (attempt < maxRetries) {
        await new Promise(resolve => setTimeout(resolve, delay * attempt));
      }
    }
  }
  
  throw lastError;
}

// Cache for profile data with TTL
interface CacheEntry {
  data: any;
  timestamp: number;
  ttl: number;
}

let profileCache: Record<string, CacheEntry> = {};

// Function to fetch user profile with timeout and retry
export async function fetchProfile(userId: string, timeoutMs: number = 15000) {
  // Check cache first
  const cached = profileCache[userId];
  if (cached && Date.now() - cached.timestamp < cached.ttl) {
    console.log('Profile found in cache for user:', userId);
    return cached.data;
  }

  console.log('Fetching profile from database for user:', userId);
  
  try {
    const result = await retryRequest(async () => {
      const { data, error } = await supabase
    .from('profiles')
    .select('*')
    .eq('id', userId)
    .maybeSingle();

    if (error) {
        console.error('Supabase error:', error);
        throw new Error(`Database error: ${error.message}`);
    }

      return data;
    });

    // Only cache a real profile — never cache null so missing profiles can be retried immediately
    if (result !== null) {
      profileCache[userId] = {
        data: result,
        timestamp: Date.now(),
        ttl: 5 * 60 * 1000 // 5 minutes
      };
    }

    console.log('Profile fetched successfully:', result ? 'found' : 'not found');
    return result;
  } catch (error) {
    console.error('Profile fetch failed after retries:', error);
      return null;
  }
}

// Function to clear profile cache
export function clearCache(userId?: string) {
  if (userId) {
    delete profileCache[userId];
  } else {
    profileCache = {};
  }
}

// Batch update helper with retry
export async function batchUpdate(table: string, updates: any[]) {
  console.log('Performing batch update on table:', table);
  const batchSize = 100;
  const batches = [];

  for (let i = 0; i < updates.length; i += batchSize) {
    const batch = updates.slice(i, i + batchSize);
    batches.push(
      retryRequest(async () => {
        const { data, error } = await supabase
        .from(table)
        .upsert(batch)
          .select();
        
        if (error) throw error;
        return data;
      })
    );
  }

  try {
    const results = await Promise.all(batches);
    console.log('Batch update completed successfully');
    return results;
  } catch (error) {
    console.error('Error in batchUpdate:', error);
    throw error;
  }
}

// Health check function
export async function checkSupabaseHealth(): Promise<boolean> {
  try {
    const { data, error } = await supabase
      .from('profiles')
      .select('count')
      .limit(1);
    
    if (error) {
      console.error('Health check failed:', error);
      return false;
    }
    
    return true;
  } catch (error) {
    console.error('Health check error:', error);
    return false;
  }
}

// Function to check and fix profile synchronization issues
export async function checkProfileSync(userId: string): Promise<{ synced: boolean; error?: string }> {
  try {
    console.log('🔍 Checking profile sync for user:', userId);
    
    // Check if profile exists
    const { data: profile, error: profileError } = await supabase
      .from('profiles')
      .select('*')
      .eq('id', userId)
      .single();
    
    if (profileError || !profile) {
      console.log('🔄 Profile missing, attempting to create...');
      
      // Get user info from auth
      const { data: { user }, error: authError } = await supabase.auth.getUser();
      if (authError || !user || user.id !== userId) {
        return { synced: false, error: 'Auth user not found or mismatch' };
      }
      
      // Create profile
      const { error: createError } = await supabase
        .from('profiles')
        .insert({
          id: userId,
          email: user.email || '',
          full_name: user.user_metadata?.full_name || '',
          avatar_url: user.user_metadata?.avatar_url || '',
          idecide_score: 0,
          has_partner: user.user_metadata?.has_partner || false,
          partner_document_access: user.user_metadata?.partner_document_access || '',
          children_age_groups: user.user_metadata?.children_age_groups || [],
          dependents: user.user_metadata?.dependents || [],
          onboarding_completed: user.user_metadata?.onboarding_completed || false,
          payment_status: 'free',
          payment_intent_id: null,
          payment_completed: true,
          payment_date: null,
          subscription_status: 'free'
        });
      
      if (createError) {
        console.error('❌ Failed to create profile:', createError);
        return { synced: false, error: createError.message };
      }
      
      console.log('✅ Profile created successfully');
      return { synced: true };
    }
    
    console.log('✅ Profile already exists');
    return { synced: true };
  } catch (error) {
    console.error('❌ Profile sync check failed:', error);
    return { synced: false, error: error instanceof Error ? error.message : 'Unknown error' };
  }
}

// Function to clear all caches and force refresh
export async function forceRefreshAuth(): Promise<void> {
  try {
    console.log('🔄 Forcing auth refresh...');
    
    // Clear profile cache
    clearCache();
    
    // Refresh session
    const { error } = await supabase.auth.refreshSession();
    if (error) {
      console.error('❌ Session refresh failed:', error);
    } else {
      console.log('✅ Session refreshed successfully');
    }
  } catch (error) {
    console.error('❌ Force refresh failed:', error);
  }
}